import React from 'react';
import {
  Activity, BarChart3, FlaskConical, Link2, MousePointerClick, Users,
} from 'lucide-react';
import { DailyActivityChart, KpiCard, LoadingSkeleton } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AffiliatesTab({ data }) {
  if (!data) return <LoadingSkeleton />;
  const affiliates = data.affiliates || [];
  const pa = data.product_analytics || {};
  const sa = data.stack_analytics || {};
  const dailyChartData = (pa.daily_trend?.length ? pa.daily_trend : data.daily_clicks || []).map(d => ({
    date: d.date, value: d.clicks, color: '#f97316',
  }));

  const GOAL_LABELS = { recovery: 'Recovery', performance: 'Performance', 'body-comp': 'Body Comp', sleep: 'Sleep', 'anti-aging': 'Anti-Aging', cognitive: 'Cognitive', gut: 'Gut Health', joint: 'Joint' };
  const ACTIVITY_LABELS = { hybrid: 'HYROX/Hybrid', crossfit: 'CrossFit', running: 'Running', strength: 'Strength', general: 'General', biohacking: 'Biohacking' };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Users} label="Total Affiliates" value={affiliates.length} color="#10B981" />
        <KpiCard icon={MousePointerClick} label="Total Clicks" value={pa.total_clicks || data.total_clicks || 0} subtext="tracked affiliate clicks" color="#06B6D4" />
        <KpiCard icon={FlaskConical} label="Stack Builder Clicks" value={sa.total_stack_clicks || 0} subtext="from peptide stack tool" color="#8B5CF6" />
        <KpiCard icon={Link2} label="Tracked Products" value={pa.by_product?.length || 0} subtext="unique products" color="#F59E0B" />
      </div>

      <DailyActivityChart data={dailyChartData} label="Daily Affiliate Clicks" />

      {/* ════════════════ PEPTIDE STACK CONVERSIONS ════════════════ */}
      {(sa.total_stack_clicks > 0 || sa.by_peptide?.length > 0) && (
        <>
          <div className="bg-white rounded-xl border-2 border-purple-100 p-6" data-testid="stack-analytics-section">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-1">
              <FlaskConical className="w-4 h-4 text-purple-500" /> Peptide Stack Builder — Revenue Attribution
            </h3>
            <p className="text-xs text-gray-400 mb-4">Clicks from personalized stack recommendations. Profile data shows which audience segments convert best.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* By Peptide */}
              {sa.by_peptide?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Top Peptides by Buy Clicks</p>
                  <div className="space-y-1.5">
                    {sa.by_peptide.map((p, i) => (
                      <div key={p.peptide} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50 last:border-0">
                        <span className="text-gray-700 font-medium">{p.peptide}</span>
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-[10px] rounded-full font-mono">{p.clicks}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* By Activity */}
              {sa.by_activity?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Clicks by Activity Type</p>
                  <div className="space-y-1.5">
                    {sa.by_activity.map((a) => (
                      <div key={a.activity} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50 last:border-0">
                        <span className="text-gray-700">{ACTIVITY_LABELS[a.activity] || a.activity}</span>
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] rounded-full font-mono">{a.clicks}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* By Sex */}
              {sa.by_sex?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Clicks by Sex</p>
                  <div className="flex gap-3">
                    {sa.by_sex.map((s) => (
                      <div key={s.sex} className="flex-1 bg-gray-50 p-3 rounded-lg text-center">
                        <p className="text-gray-700 text-xs capitalize font-medium">{s.sex}</p>
                        <p className="text-gray-900 text-lg font-bold font-mono">{s.clicks}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* By Age Range */}
              {sa.by_age?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Clicks by Age Range</p>
                  <div className="flex gap-2 flex-wrap">
                    {sa.by_age.map((a, i) => (
                      <div key={a.age} className="bg-gray-50 px-3 py-2 rounded-lg text-center">
                        <p className="text-gray-500 text-[10px]">{a.age}</p>
                        <p className="text-gray-900 text-sm font-bold font-mono">{a.clicks}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* By Goal */}
              {sa.by_goal?.length > 0 && (
                <div className="md:col-span-2">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Clicks by Goal</p>
                  <div className="flex gap-2 flex-wrap">
                    {sa.by_goal.map((g, i) => (
                      <span key={g.clicks} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs rounded-full font-medium">
                        {GOAL_LABELS[g.goal] || g.goal} <strong className="font-mono">{g.clicks}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Profile Combos */}
              {sa.top_profiles?.length > 0 && (
                <div className="md:col-span-2">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Top User Profiles (Sex / Age / Activity)</p>
                  <div className="space-y-1.5">
                    {sa.top_profiles.map((c) => (
                      <div key={c.profile} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50 last:border-0">
                        <span className="text-gray-700 font-mono bg-gray-50 px-2 py-0.5 rounded">{c.profile}</span>
                        <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-[10px] rounded-full font-mono">{c.clicks} clicks</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Product-level analytics */}
      {pa.by_product?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <MousePointerClick className="w-4 h-4 text-cyan-500" /> Top Products by Clicks
          </h3>
          <div className="space-y-2">
            {pa.by_product.map((p) => (
              <div key={p.product || 'unknown'} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                <span className="text-gray-700 text-xs font-medium">{p.product || 'Unknown'}</span>
                <span className="px-2 py-0.5 bg-cyan-50 text-cyan-600 text-xs rounded-full font-mono">{p.clicks} clicks</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Clicks by source page */}
      {pa.by_source?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-purple-500" /> Top Pages (Affiliate Clicks)
          </h3>
          <div className="space-y-2">
            {pa.by_source.map((s, i) => (
              <div key={s.page} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                <span className="text-gray-700 truncate mr-3 text-xs">{s.page || '/'}</span>
                <span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded-full font-mono">{s.clicks} clicks</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {pa.by_url?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Link2 className="w-4 h-4 text-orange-500" /> Top Affiliate Destinations
          </h3>
          <div className="space-y-2">
            {pa.by_url.map((l, i) => (
              <div key={l.url} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                <div className="truncate mr-3">
                  <span className="text-gray-700 text-xs">{l.url}</span>
                  {l.products?.length > 0 && (
                    <span className="text-gray-400 text-[10px] ml-2">({l.products.join(', ')})</span>
                  )}
                </div>
                <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-xs rounded-full font-mono">{l.clicks} clicks</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fallback: old top_links data */}
      {!pa.by_url?.length && data.top_links?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Link2 className="w-4 h-4 text-orange-500" /> Top Affiliate Destinations
          </h3>
          <div className="space-y-2">
            {data.top_links.map((l, i) => (
              <div key={l.url} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                <span className="text-gray-700 truncate mr-3 text-xs">{l.url}</span>
                <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-xs rounded-full font-mono">{l.clicks} clicks</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-teal-500" /> Affiliate Sign-ups
        </h3>
        {affiliates.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-400 text-xs border-b border-gray-100">
                <th className="pb-2 font-medium">Name</th><th className="pb-2 font-medium">Email</th><th className="pb-2 font-medium">Niche</th><th className="pb-2 font-medium">Followers</th><th className="pb-2 font-medium">Socials</th>
              </tr></thead>
              <tbody>
                {affiliates.map((a, i) => (
                  <tr key={a.email} className="border-b border-gray-50 last:border-0">
                    <td className="py-2.5 text-gray-700">{a.first_name} {a.last_name}</td>
                    <td className="py-2.5 text-gray-500">{a.email}</td>
                    <td className="py-2.5"><span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded-full">{a.sport_niche || '-'}</span></td>
                    <td className="py-2.5 text-gray-500">{a.follower_count || '-'}</td>
                    <td className="py-2.5 text-gray-400 text-xs">
                      {[a.instagram && 'IG', a.tiktok && 'TT', a.youtube && 'YT', a.twitter && 'X'].filter(Boolean).join(', ') || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-gray-400 text-sm">No affiliate sign-ups yet</p>}
      </div>
    </div>
  );
}
